// DTOs de C13 — gestión de usuarios (líderes y supervisores) del scope
// del coordinador.
//
// El coordinador NO crea cuentas con password. Asume que el user ya existe
// en `users` (creado por super admin o auto-registro). El POST de C13 lo
// inscribe en `tenant_users` con rol lider o supervisor, y le asigna
// líneas (si líder) o plantas (si supervisor).
//
// Cambios de estado requieren razón ≥ 20 chars (queda en eventos_auditoria).
//
// Endpoints:
//   - GET    /api/coordinador/configuracion/usuarios
//   - POST   /api/coordinador/configuracion/usuarios
//   - PATCH  /api/coordinador/configuracion/usuarios/:user_id (activo + nombre)
//   - PUT    /api/coordinador/configuracion/usuarios/:user_id/lineas (líder)
//
// NOTA: el coordinador NO reasigna plantas a supervisores ni mueve personas
// entre plantas (esa autoridad es del Admin Tenant). La planta del supervisor
// se fija al inscribirlo y queda inmutable desde C13.
import { z } from 'zod';
const razonSchema = z.string().min(20, 'la razón debe tener al menos 20 caracteres').max(500);
// =============================================================================
// Recursos
// =============================================================================
const plantaResumenSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
});
const lineaResumenSchema = z.object({
    id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
});
// =============================================================================
// Usuario (líder o supervisor)
// =============================================================================
export const usuarioGestionSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().email(),
    /** Cédula = parte local del email sintético, si es numérica. null si el login es por correo real. */
    cedula: z.string().nullable(),
    /** Teléfono/celular de contacto. null si no cargado. */
    telefono: z.string().nullable(),
    nombre: z.string(),
    apellido: z.string(),
    rol: z.enum(['lider', 'supervisor']),
    /** activo en tenant_users. */
    activo: z.boolean(),
    /** Líneas asignadas (solo aplica a líderes). */
    lineas_asignadas: z.array(z.object({
        linea_id: z.string().uuid(),
        linea_codigo: z.string(),
        linea_nombre: z.string(),
        planta_nombre: z.string(),
    })),
    /** Plantas asignadas (solo aplica a supervisores). */
    plantas_asignadas: z.array(z.object({
        planta_id: z.string().uuid(),
        planta_nombre: z.string(),
        planta_codigo: z.string(),
    })),
    created_at: z.string(),
    updated_at: z.string(),
});
// =============================================================================
// Payloads
// =============================================================================
// Dos modos de alta (backward-compatible):
//  - por correo (email): comportamiento previo (invite / user existente).
//  - por cédula + contraseña (cedula + password_temporal): crea login directo
//    sin buzón — el email se sintetiza {cedula}@dominio. Para rotación de personal.
export const usuarioCreateSchema = z
    .object({
    email: z.string().email().optional(),
    cedula: z
        .string()
        .regex(/^\d{6,12}$/, 'La cédula debe ser de 6 a 12 dígitos numéricos')
        .optional(),
    password_temporal: z.string().min(8, 'Mínimo 8 caracteres').max(72).optional(),
    telefono: z.string().trim().min(7, 'El teléfono es obligatorio').max(30),
    nombre: z.string().min(1).max(100),
    apellido: z.string().min(1).max(100),
    rol: z.enum(['lider', 'supervisor']),
    /** Si rol=lider: líneas iniciales (deben estar en scope del coordinador). */
    linea_ids: z.array(z.string().uuid()).optional(),
    /** Si rol=supervisor: plantas iniciales. */
    planta_ids: z.array(z.string().uuid()).optional(),
    razon: razonSchema,
})
    .refine((d) => Boolean(d.email) || (Boolean(d.cedula) && Boolean(d.password_temporal)), {
    message: 'Indique un correo, o cédula + contraseña temporal',
    path: ['cedula'],
});
export const usuarioUpdateSchema = z.object({
    nombre: z.string().min(1).max(100).optional(),
    apellido: z.string().min(1).max(100).optional(),
    telefono: z.string().trim().min(7, 'Teléfono inválido').max(30).optional(),
    activo: z.boolean().optional(),
    razon: razonSchema,
});
// Reset de contraseña (solo líderes del scope del coordinador).
export const usuarioResetPasswordSchema = z.object({
    password_temporal: z.string().min(8, 'Mínimo 8 caracteres').max(72),
    razon: razonSchema,
});
export const usuarioReasignarLineasSchema = z.object({
    linea_ids: z.array(z.string().uuid()),
    razon: razonSchema,
});
// =============================================================================
// Response: lista
// =============================================================================
export const coordinadorUsuariosListSchema = z.object({
    user: z.object({
        id: z.string().uuid(),
        email: z.string().email(),
        nombre: z.string(),
        apellido: z.string(),
    }),
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    recursos: z.object({
        plantas: z.array(plantaResumenSchema),
        lineas: z.array(lineaResumenSchema),
    }),
    usuarios: z.array(usuarioGestionSchema),
});
//# sourceMappingURL=coordinador_configuracion_usuarios.js.map